<template>
  <v-form ref="clientForm" lazy-validation>
    <!-- Toggle -->
    <v-row class="mt-3">
      <v-col cols="12" sm="6">
        <v-btn
          block
          min-height="200px"
          color="primary"
          style="font-size: 30px"
          :outlined="newClientOutlined"
          @click="newClientClickedHandler"
        >
          New<br>Client
        </v-btn>
      </v-col>

      <v-col cols="12" sm="6">
        <v-btn
          block
          min-height="200px"
          color="primary"
          style="font-size: 30px"
          :outlined="existingClientOutlined"
          @click="existingClientClickedHandler"
        >
          Existing<br>Client
        </v-btn>
      </v-col>
    </v-row>

    <!-- Existing client dropdown -->
    <v-row>
      <v-col cols="12" sm="6" lg="3">
        <BusinessEntitiesDropDownList
          :is-required="true"
          :value="businessEntityId"
          :readonly="readonly"
          @businessEntityIdChange="businessEntityId = $event"
        />
      </v-col>
      <v-col cols="12" sm="6" lg="3" v-if="isExistingClient">
        <ClientsDropDownList
          :is-required="isExistingClient"
          :id="existingClientId"
          :business-entity-id="businessEntityId"
          :readonly="readonly"
          @clientIdChange="existingClientId = $event"
        />
      </v-col>
    </v-row>

    <!-- Fields -->
    <v-row>
      <v-col cols="12" sm="6" md="3" xl="2">
        <v-text-field
          outlined
          label="Name"
          v-model="name"
          :rules="[Required('Client Name')]"
          :readonly="isExistingClient || readonly"
          :disabled="noClientOptionSelected"
        />
      </v-col>

      <v-col cols="12" sm="6" md="3" xl="2">
        <InvoiceFrequenciesDropDownList
          :id="invoiceFrequencyId"
          :readOnly="isExistingClient || readonly"
          :disabled="noClientOptionSelected"
          :clearable="!readonly && !isExistingClient"
          :isRequired="true"
          @change="invoiceFrequencyId = $event"
        />
      </v-col>

      <v-col cols="12" sm="6" md="3" lg="3" xl="2">
        <ThirdPartyPortalsDropDownList
          :id="thirdPartyPortalId"
          :disabled="noClientOptionSelected"
          :readonly="isExistingClient || readonly"
          :clearable="!readonly && !isExistingClient"
          @ThirdPartyPortalIdChange="thirdPartyPortalId = $event"
        />
      </v-col>

      <v-col cols="12" sm="6" md="3" xl="2">
        <FormattedNumberField
          label="VMS Fee"
          :value="vmsFee"
          append-icon="percent"
          :disabled="noClientOptionSelected"
          :clearable="!readonly"
          :readonly="isExistingClient || readonly"
          @valueChange="vmsFee = $event"
        />
      </v-col>
    </v-row>
    <v-row class="pt-0 pb-5">
      <v-col cols="12" class="pt-0">
        <v-checkbox
          v-model="useStaffLynxForTimeEntering"
          label="Use StaffLynx for Time Entry"
          :disabled="noClientOptionSelected"
          :readonly="isExistingClient || readonly"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { ClientsDropDownList, ThirdPartyPortalsDropDownList } from '@/components/clients'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { InvoiceFrequenciesDropDownList } from '@/components/invoiceFrequencies'
import { FormattedNumberField } from '@/components/shared/textFields'
import Required from '@/lib/formValidations/required'
import { NEW_CLIENT, EXISTING_CLIENT } from '@/lib/constants/clientTypes'
import { PROJECT } from '@/lib/constants/dealAssignmentTypes'

export default {
  props: [
    'client',
    'readonly'
  ],

  components: {
    ClientsDropDownList,
    BusinessEntitiesDropDownList,
    InvoiceFrequenciesDropDownList,
    ThirdPartyPortalsDropDownList,
    FormattedNumberField
  },

  data () {
    return {
      NEW_CLIENT,
      EXISTING_CLIENT,
      PROJECT
    }
  },

  computed: {
    isExistingClient () {
      return this.client &&
        (this.client.clientTypeId === this.EXISTING_CLIENT ||
        !!this.client.existingClientId)
    },

    isNewClient () {
      return (!this.client.existingClientId &&
        (!!this.client.name ||
        !!this.client.invoiceFrequencyId ||
        !!this.client.thirdPartyPortalId ||
        !!this.client.vmsFee
        )) ||
        this.client.clientTypeId === this.NEW_CLIENT
    },

    newClientOutlined () {
      return !this.isNewClient ||
      (!!this.client.clientTypeId && this.client.clientTypeId !== this.NEW_CLIENT)
    },

    existingClientOutlined () {
      return !(
        this.client.clientTypeId === this.EXISTING_CLIENT ||
        this.client.existingClientId
      )
    },

    noClientOptionSelected () {
      return this.newClientOutlined && this.existingClientOutlined
    },

    clientTypeId: {
      get () {
        return this.client ? this.client.clientTypeId : null
      },
      set (value) {
        this.$emit('setClientTypeId', value)
      }
    },

    existingClientId: {
      get () {
        return this.client ? this.client.existingClientId : null
      },
      set (value) {
        this.$emit('setExistingClientId', value)
      }
    },

    businessEntityId: {
      get () {
        return this.client ? this.client.businessEntityId : null
      },
      set (value) {
        this.$emit('setBusinessEntityId', value)
      }
    },

    name: {
      get () {
        return this.client ? this.client.name : ''
      },
      set (value) {
        this.$emit('setClientName', value)
      }
    },

    invoiceFrequencyId: {
      get () {
        return this.client ? this.client.invoiceFrequencyId : null
      },
      set (value) {
        this.$emit('setInvoiceFrequencyId', value)
      }
    },

    thirdPartyPortalId: {
      get () {
        return this.client ? this.client.thirdPartyPortalId : null
      },
      set (value) {
        this.$emit('setThirdPartyPortalId', value)
      }
    },

    vmsFee: {
      get () {
        return this.client ? this.client.vmsFee : null
      },
      set (value) {
        this.$emit('setVmsFee', value)
      }
    },

    useStaffLynxForTimeEntering: {
      get () {
        return this.client ? this.client.useStaffLynxForTimeEntering : false
      },
      set (value) {
        this.$emit('setUseStaffLynxForTimeEntering', value)
      }
    }
  },

  methods: {
    Required,

    newClientClickedHandler () {
      if (!this.readonly) {
        this.resetFormValidation()
        this.clientTypeId = this.NEW_CLIENT
      }
    },

    existingClientClickedHandler () {
      if (!this.readonly) {
        this.resetFormValidation()
        this.clientTypeId = this.EXISTING_CLIENT
      }
    },

    resetFormValidation () {
      if (this.$refs.clientForm) {
        this.$refs.clientForm.resetValidation()
      }
    },
    isClientValid () {
      return this.$refs.clientForm.validate() ? true : 'Please fill out the required fields to proceed'
    }
  },

  mounted () {
    this.resetFormValidation()
  }
}
</script>
